import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getScans, getStats, getReports, getDeviceStats, checkAdmin, scanUrl, csvUrl } from "../lib/api";
import { Search, RefreshCw, Download, Flag, ShieldCheck, Monitor, LogIn, ShieldOff } from "lucide-react";

const VERDICT_BG = { safe: "#00B259", suspicious: "#FFC400", dangerous: "#E53935" };
const VERDICT_FG = { safe: "#fff", suspicious: "#050505", dangerous: "#fff" };

function Badge({ verdict }) {
  return (
    <span
      className="text-[10px] font-bold tracking-[0.2em] uppercase px-2 py-1 border-2 border-black"
      style={{ background: VERDICT_BG[verdict], color: VERDICT_FG[verdict] }}
      data-testid={`badge-${verdict}`}
    >
      {verdict}
    </span>
  );
}

export default function Dashboard() {
  const [token, setToken] = useState(() => sessionStorage.getItem("wg_admin_token") || "");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(false);
  const [authMsg, setAuthMsg] = useState("");
  const [stats, setStats] = useState(null);
  const [scans, setScans] = useState([]);
  const [reports, setReports] = useState([]);
  const [devices, setDevices] = useState(null);
  const [loading, setLoading] = useState(false);
  const [url, setUrl] = useState("");
  const [scanning, setScanning] = useState(false);

  const tryAuth = async (t) => {
    setChecking(true); setAuthMsg("");
    try {
      const r = await checkAdmin(t);
      if (r.ok) {
        setAuthed(true);
        sessionStorage.setItem("wg_admin_token", t);
        window.dispatchEvent(new Event("wg-admin-changed"));
      } else { setAuthed(false); setAuthMsg("Invalid admin token."); }
    } catch (e) { setAuthed(false); setAuthMsg("Invalid admin token."); }
    finally { setChecking(false); }
  };

  useEffect(() => { if (token) tryAuth(token); /* eslint-disable-next-line */ }, []);

  const load = async () => {
    if (!authed) return;
    setLoading(true);
    try {
      const [s, l, r, d] = await Promise.all([
        getStats(),
        getScans(50, token),
        getReports(15, token),
        getDeviceStats(token),
      ]);
      setStats(s); setScans(l); setReports(r); setDevices(d);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (authed) load(); /* eslint-disable-next-line */ }, [authed]);

  const onLogin = (e) => { e.preventDefault(); if (token.trim()) tryAuth(token.trim()); };
  const logout = () => {
    sessionStorage.removeItem("wg_admin_token");
    window.dispatchEvent(new Event("wg-admin-changed"));
    setToken(""); setAuthed(false);
  };

  const onScan = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setScanning(true);
    try {
      await scanUrl(url.trim());
      setUrl("");
      await load();
    } finally { setScanning(false); }
  };

  if (!authed) {
    return (
      <div className="max-w-[560px] mx-auto px-6 py-16">
        <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500">Admin Dashboard</div>
        <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tighter" style={{ fontFamily: "'Clash Display'" }}>Admin Only</h1>
        <p className="mt-3 text-sm text-neutral-700">
          This dashboard shows <strong>global</strong> data — scans from every device, community reports, install counts, and exports. End-users see their personal dashboard at <Link className="underline" to="/my">/my</Link> instead.
        </p>
        <form onSubmit={onLogin} className="mt-6 brutal-card p-6" data-testid="admin-dashboard-login-form">
          <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-neutral-500">
            <LogIn size={14} /> Admin Token
          </div>
          <input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="enter admin token"
            className="mt-3 w-full border-2 border-black bg-white px-3 py-3 font-mono text-sm rounded-none focus:outline-none focus:bg-black/5"
            data-testid="admin-dashboard-token-input"
            autoFocus
          />
          <div className="mt-4 flex items-center gap-3">
            <button className="brutal-btn" disabled={checking} data-testid="admin-dashboard-login-btn">
              {checking ? "Checking…" : "Unlock"}
            </button>
            {authMsg && <div className="text-xs uppercase tracking-widest font-bold text-[#E53935]" data-testid="admin-dashboard-error">{authMsg}</div>}
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500">Operations Console</div>
          <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tighter" style={{ fontFamily: "'Clash Display'" }}>Dashboard</h1>
        </div>
        <div className="flex gap-2">
          <button
            onClick={async () => {
              const resp = await fetch(csvUrl, { headers: { "X-Admin-Token": token } });
              const blob = await resp.blob();
              const a = document.createElement("a");
              a.href = URL.createObjectURL(blob);
              a.download = "wallgard-scans.csv";
              a.click();
              URL.revokeObjectURL(a.href);
            }}
            className="brutal-btn brutal-btn-outline" data-testid="dashboard-export-csv">
            <Download size={14} /> Export CSV
          </button>
          <button onClick={load} className="brutal-btn brutal-btn-outline" data-testid="dashboard-refresh">
            <RefreshCw size={14} /> Refresh
          </button>
          <button onClick={logout} className="brutal-btn brutal-btn-outline" data-testid="admin-dashboard-logout">
            <ShieldOff size={14} /> Lock
          </button>
        </div>
      </div>

      {/* Stats grid */}
      <div className="mt-8 grid grid-cols-2 md:grid-cols-5 border-2 border-black bg-white" data-testid="stats-grid">
        {[
          { label: "Total Scans", value: stats?.total ?? 0, bg: "#fff", fg: "#050505" },
          { label: "Genuine Sites", value: stats?.safe ?? 0, bg: "#00B259", fg: "#fff" },
          { label: "Suspicious", value: stats?.suspicious ?? 0, bg: "#FFC400", fg: "#050505" },
          { label: "Threats Blocked", value: stats?.dangerous ?? 0, bg: "#E53935", fg: "#fff" },
          { label: "User Reports", value: stats?.reports ?? 0, bg: "#050505", fg: "#fff" },
        ].map((s, i) => (
          <div key={i} className={`p-6 ${i < 4 ? "border-r-2 border-black" : ""}`} style={{ background: s.bg, color: s.fg }} data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g,'-')}`}>
            <div className="text-[10px] tracking-[0.3em] uppercase opacity-80">{s.label}</div>
            <div className="text-5xl font-black tracking-tighter mt-2" style={{ fontFamily: "'Clash Display'" }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Manual scan */}
      <form onSubmit={onScan} className="mt-8 brutal-card p-2 flex items-center gap-2" data-testid="manual-scan-form">
        <Search size={18} className="ml-2" />
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="Scan a URL manually…"
          className="flex-1 px-2 py-3 outline-none bg-transparent font-mono text-sm"
          data-testid="manual-scan-input"
        />
        <button className="brutal-btn" disabled={scanning} data-testid="manual-scan-btn">{scanning ? "Scanning…" : "Scan"}</button>
      </form>
      {scanning && <div className="scan-stripes h-2 mt-2" />}

      {/* Installs / Device telemetry */}
      <div className="mt-8 brutal-card p-6" data-testid="installs-block">
        <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-3 flex items-center gap-2">
          <Monitor size={12}/> Extension Installs · Live Telemetry
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border-2 border-black">
          {[
            { label: "Total Devices", value: devices?.total ?? 0, bg: "#050505", fg: "#fff", testid: "device-total" },
            { label: "Active 24h", value: devices?.dau ?? 0, bg: "#00B259", fg: "#fff", testid: "device-dau" },
            { label: "Active 7d", value: devices?.wau ?? 0, bg: "#FFC400", fg: "#050505", testid: "device-wau" },
            { label: "Active 30d", value: devices?.mau ?? 0, bg: "#fff", fg: "#050505", testid: "device-mau" },
            { label: "New Today", value: devices?.new_today ?? 0, bg: "#fff", fg: "#050505", testid: "device-new-today" },
          ].map((s, i) => (
            <div key={i} className={`p-4 ${i < 4 ? "border-r-2 border-black" : ""}`} style={{ background: s.bg, color: s.fg }} data-testid={s.testid}>
              <div className="text-[10px] tracking-[0.3em] uppercase opacity-80">{s.label}</div>
              <div className="text-3xl font-black tracking-tighter mt-1" style={{ fontFamily: "'Clash Display'" }}>{s.value}</div>
            </div>
          ))}
        </div>
        {devices && Object.keys(devices.versions || {}).length > 0 && (
          <div className="mt-4">
            <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-2">Version Distribution</div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(devices.versions).map(([v, n]) => (
                <div key={v} className="border-2 border-black px-3 py-2 flex items-center gap-3">
                  <span className="text-[10px] tracking-widest uppercase font-bold font-mono">v{v}</span>
                  <span className="bg-black text-white px-2 py-0.5 text-xs font-bold">{n}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Two-column: Categories & Community Reports */}
      <div className="mt-8 grid lg:grid-cols-2 gap-6">
        {stats && Object.keys(stats.categories || {}).length > 0 && (
          <div className="brutal-card p-6" data-testid="categories-block">
            <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-3 flex items-center gap-2">
              <ShieldCheck size={12}/> Threat Category Breakdown
            </div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats.categories).map(([k, v]) => (
                <div key={k} className="border-2 border-black px-3 py-2 flex items-center gap-3">
                  <span className="text-[10px] tracking-widest uppercase font-bold">{k}</span>
                  <span className="bg-black text-white px-2 py-0.5 text-xs font-bold">{v}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="brutal-card p-6" data-testid="community-reports-block">
          <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-3 flex items-center gap-2">
            <Flag size={12}/> Community Reports · Top Hosts
          </div>
          {reports.length === 0 ? (
            <div className="text-sm text-neutral-500">No reports yet. Users can report suspicious sites from the extension popup or warning overlay.</div>
          ) : (
            <table className="w-full">
              <thead>
                <tr>
                  <th className="text-left text-[10px] uppercase tracking-[0.2em] pb-2">Host</th>
                  <th className="text-left text-[10px] uppercase tracking-[0.2em] pb-2">Reports</th>
                  <th className="text-left text-[10px] uppercase tracking-[0.2em] pb-2">Last</th>
                  <th className="text-left text-[10px] uppercase tracking-[0.2em] pb-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((r) => (
                  <tr key={r.host} className="border-t border-black/20">
                    <td className="py-2 font-mono text-xs max-w-[260px] truncate">{r.host}</td>
                    <td className="py-2 font-bold">{r.count}</td>
                    <td className="py-2 text-xs">{new Date(r.last_reported).toLocaleDateString()}</td>
                    <td className="py-2">
                      <span className="text-[10px] font-bold tracking-widest uppercase px-2 py-1 border-2 border-black"
                            style={{ background: r.promoted ? "#E53935" : "#FFC400", color: r.promoted ? "#fff" : "#050505" }}>
                        {r.promoted ? "Auto-blocked" : "Watch"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Scan history table */}
      <div className="mt-8 brutal-card overflow-x-auto" data-testid="scans-table">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-neutral-100">
              <th className="text-left text-[10px] uppercase tracking-[0.2em] p-3 border-b-2 border-black border-r-2">Time</th>
              <th className="text-left text-[10px] uppercase tracking-[0.2em] p-3 border-b-2 border-black border-r-2">Verdict</th>
              <th className="text-left text-[10px] uppercase tracking-[0.2em] p-3 border-b-2 border-black border-r-2">Host</th>
              <th className="text-left text-[10px] uppercase tracking-[0.2em] p-3 border-b-2 border-black border-r-2">Risk</th>
              <th className="text-left text-[10px] uppercase tracking-[0.2em] p-3 border-b-2 border-black border-r-2">Categories</th>
              <th className="text-left text-[10px] uppercase tracking-[0.2em] p-3 border-b-2 border-black">Reason</th>
            </tr>
          </thead>
          <tbody>
            {scans.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-sm text-neutral-500">{loading ? "Loading…" : "No scans yet. Run a scan above or use the extension."}</td></tr>
            )}
            {scans.map((s) => (
              <tr key={s.id} data-testid={`scan-row-${s.id}`}>
                <td className="p-3 border-b border-black/20 border-r-2 border-r-black/20 font-mono text-xs">{new Date(s.timestamp).toLocaleString()}</td>
                <td className="p-3 border-b border-black/20 border-r-2 border-r-black/20"><Badge verdict={s.verdict} /></td>
                <td className="p-3 border-b border-black/20 border-r-2 border-r-black/20 font-mono text-xs max-w-[260px] truncate">{s.host}</td>
                <td className="p-3 border-b border-black/20 border-r-2 border-r-black/20 font-bold">{s.risk}</td>
                <td className="p-3 border-b border-black/20 border-r-2 border-r-black/20">
                  <div className="flex flex-wrap gap-1">
                    {(s.categories || []).map((c) => (
                      <span key={c} className="text-[10px] border-2 border-black px-1.5 py-0.5 uppercase">{c}</span>
                    ))}
                  </div>
                </td>
                <td className="p-3 border-b border-black/20 text-xs max-w-[420px]">{s.ai_reason || s.findings?.[0]?.detail || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
